import React, { useState, useRef } from "react"
import moment from "moment"
import MonthSelector from "./MonthSelector"
import WeekSelector from "./WeekSelector"
import { DATE_PERIODS_TYPES } from "constants/DatePeriods"

const DatePeriodsSelector = ({
   onChange,
   disabled = false,
   isLoading = false,
   minDate,
   maxDate,
   productionLineId,
}) => {
   const getProductionLineDateRangeKey = (min, max, lineId) => {
      const minKey = min ? moment(min).startOf("day").valueOf() : "x"
      const maxKey = max ? moment(max).startOf("day").valueOf() : "x"
      const lineKey = lineId ?? "x"
      return `${lineKey}|${minKey}|${maxKey}`
   }

   const calculateInitialState = (min, max) => {
      if (!min) {
         return { month: null, weeks: [], productionLineId }
      }

      const currentMonth = moment().startOf(DATE_PERIODS_TYPES.MONTH)
      const startDate = moment(min)
      const endDate = max ? moment(max) : null

      let selectedMonthStart
      let selectedWeekStart

      if (startDate.isSame(currentMonth, DATE_PERIODS_TYPES.MONTH)) {
         selectedMonthStart = currentMonth
         selectedWeekStart = startDate.clone().startOf(DATE_PERIODS_TYPES.WEEK)
      } else if (
         endDate &&
         currentMonth.isBetween(
            startDate,
            endDate,
            DATE_PERIODS_TYPES.MONTH,
            "[]"
         )
      ) {
         selectedMonthStart = currentMonth
         selectedWeekStart = currentMonth
            .clone()
            .startOf(DATE_PERIODS_TYPES.MONTH)
            .startOf(DATE_PERIODS_TYPES.WEEK)
      } else {
         selectedMonthStart = startDate
            .clone()
            .startOf(DATE_PERIODS_TYPES.MONTH)
         selectedWeekStart = startDate.clone().startOf(DATE_PERIODS_TYPES.WEEK)
      }

      const monthOption = {
         value: selectedMonthStart.toDate(),
         label: selectedMonthStart.format("MMMM YYYY"),
         startDate: selectedMonthStart
            .clone()
            .startOf(DATE_PERIODS_TYPES.MONTH)
            .toDate(),
         endDate: selectedMonthStart
            .clone()
            .endOf(DATE_PERIODS_TYPES.MONTH)
            .toDate(),
      }

      const firstWeekEnd = selectedWeekStart
         .clone()
         .endOf(DATE_PERIODS_TYPES.WEEK)

      const monthStartForLabel = selectedMonthStart
         .clone()
         .startOf(DATE_PERIODS_TYPES.MONTH)
      const relWeekNumber =
         selectedWeekStart.diff(
            monthStartForLabel.clone().startOf(DATE_PERIODS_TYPES.WEEK),
            DATE_PERIODS_TYPES.WEEK
         ) + 1

      const weekOption = {
         value: selectedWeekStart.toDate(),
         label: `Week ${relWeekNumber}: ${selectedWeekStart.format(
            "MMM DD"
         )} - ${firstWeekEnd.format("MMM DD")}`,
         startDate: selectedWeekStart.toDate(),
         endDate: firstWeekEnd.toDate(),
         weekNumber: relWeekNumber,
      }

      return { month: monthOption, weeks: [weekOption], productionLineId }
   }

   const [state, setState] = useState(() =>
      calculateInitialState(minDate, maxDate)
   )

   const lastProdLineDateRangeKeyRef = useRef("")
   const currentProductionLineDateRangeKey = getProductionLineDateRangeKey(
      minDate,
      maxDate,
      productionLineId
   )

   if (lastProdLineDateRangeKeyRef.current === "") {
      lastProdLineDateRangeKeyRef.current = currentProductionLineDateRangeKey

      if (state.weeks.length > 0) {
         const initialWeeks = state.weeks
         queueMicrotask(() => {
            onChange({
               selections: initialWeeks.map(week => ({
                  type: DATE_PERIODS_TYPES.WEEK,
                  value: week.value,
                  startDate: week.startDate,
                  endDate: week.endDate,
                  key: "selection",
               })),
            })
         })
      }
   } else if (
      lastProdLineDateRangeKeyRef.current !== currentProductionLineDateRangeKey
   ) {
      lastProdLineDateRangeKeyRef.current = currentProductionLineDateRangeKey

      if (!minDate) {
         setState({ month: null, weeks: [], productionLineId })
      } else {
         const newState = calculateInitialState(minDate, maxDate)
         setState(newState)

         const nextWeeks = newState.weeks
         if (nextWeeks.length > 0) {
            queueMicrotask(() => {
               onChange({
                  selections: nextWeeks.map(week => ({
                     type: DATE_PERIODS_TYPES.WEEK,
                     value: week.value,
                     startDate: week.startDate,
                     endDate: week.endDate,
                     key: "selection",
                  })),
               })
            })
         }
      }
   }

   const handleMonthChange = month => {
      const startDate = moment(minDate)
      const selectedMonthStart = moment(month.startDate)
      const currentMonth = moment().startOf(DATE_PERIODS_TYPES.MONTH)
      const endDate = maxDate ? moment(maxDate) : null

      let selectedWeekStart

      if (selectedMonthStart.isSame(startDate, DATE_PERIODS_TYPES.MONTH)) {
         selectedWeekStart = startDate.clone().startOf(DATE_PERIODS_TYPES.WEEK)
      } else if (
         selectedMonthStart.isSame(currentMonth, DATE_PERIODS_TYPES.MONTH) &&
         endDate &&
         currentMonth.isBetween(
            startDate,
            endDate,
            DATE_PERIODS_TYPES.MONTH,
            "[]"
         )
      ) {
         selectedWeekStart = currentMonth
            .clone()
            .startOf(DATE_PERIODS_TYPES.MONTH)
            .startOf(DATE_PERIODS_TYPES.WEEK)
      } else {
         selectedWeekStart = selectedMonthStart
            .clone()
            .startOf(DATE_PERIODS_TYPES.MONTH)
            .startOf(DATE_PERIODS_TYPES.WEEK)
      }

      const firstWeekEnd = selectedWeekStart
         .clone()
         .endOf(DATE_PERIODS_TYPES.WEEK)

      const monthStartForLabel = selectedMonthStart
         .clone()
         .startOf(DATE_PERIODS_TYPES.MONTH)
      const relWeekNumber =
         selectedWeekStart.diff(
            monthStartForLabel.clone().startOf(DATE_PERIODS_TYPES.WEEK),
            DATE_PERIODS_TYPES.WEEK
         ) + 1

      const firstWeekOption = {
         value: selectedWeekStart.toDate(),
         label: `Week ${relWeekNumber}: ${selectedWeekStart.format(
            "MMM DD"
         )} - ${firstWeekEnd.format("MMM DD")}`,
         startDate: selectedWeekStart.toDate(),
         endDate: firstWeekEnd.toDate(),
         weekNumber: relWeekNumber,
      }

      setState({
         month,
         weeks: [firstWeekOption],
         productionLineId: state.productionLineId,
      })

      onChange({
         selections: [
            {
               type: DATE_PERIODS_TYPES.WEEK,
               value: firstWeekOption.value,
               startDate: firstWeekOption.startDate,
               endDate: firstWeekOption.endDate,
               key: "selection",
            },
         ],
      })
   }

   const handleWeekChange = weeks => {
      setState({
         month: state.month,
         weeks,
         productionLineId: state.productionLineId,
      })

      const formattedWeeks = weeks.map(week => ({
         type: DATE_PERIODS_TYPES.WEEK,
         value: week.value,
         startDate: week.startDate,
         endDate: week.endDate,
         key: "selection",
      }))

      onChange({ selections: formattedWeeks })
   }

   return (
      <div className="dual-period-selector">
         <div className="month-selector-wrapper">
            <MonthSelector
               selectedMonth={state.month}
               onChange={handleMonthChange}
               disabled={disabled}
               isLoading={isLoading}
               minDate={minDate}
               maxDate={maxDate}
               placeholder="Select Month"
               productionLineId={productionLineId}
            />
         </div>

         <div className="week-selector-wrapper">
            <WeekSelector
               selectedWeeks={state.weeks}
               onChange={handleWeekChange}
               disabled={disabled || !state.month}
               isLoading={isLoading}
               selectedMonth={state.month}
               placeholder="Select Weeks"
               productionLineId={productionLineId}
               startDate={minDate}
            />
         </div>
      </div>
   )
}

export default DatePeriodsSelector
